import './BenefitsByTierMatrix.css';

import { Crown, Pencil, Check } from 'lucide-react';
import { resolveThemeToken } from '../../themeTokens';
import type { MembershipTypeRow } from '../../api/membership.api';

type Props = {
  tiers: MembershipTypeRow[];
  onEditBenefits?: () => void;
};

/**
 * Canonical loyalty benefits compared across tiers. The set is intentionally
 * fixed for Stage 1 — merchants edit each tier's perks in the creator wizard;
 * the matrix simply highlights which canonical benefits each tier already
 * advertises by matching against the tier's `perksJson` array.
 */
const CANONICAL_BENEFITS: ReadonlyArray<string> = [
  'Priority queue skip',
  'Points multiplier',
  'Birthday bonus',
  'Free service / month',
  'Early booking access',
  'Partner offers',
  'Referral bonus',
  'Dedicated support',
];

const parsePerks = (raw?: string): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((perk) => String(perk || '').trim()).filter(Boolean);
  } catch (_e) {
    return [];
  }
};

const tierAdvertisesBenefit = (perks: string[], benefit: string): boolean => {
  const target = benefit.toLowerCase();
  return perks.some((perk) => perk.toLowerCase().includes(target));
};

const BenefitsByTierMatrix = ({ tiers, onEditBenefits }: Props) => {
  const activeTiers = tiers.filter((tier) => tier.isActive !== false);
  if (!activeTiers.length) return null;

  const ordered = [...activeTiers].sort(
    (left, right) => (left.tierLevel ?? 0) - (right.tierLevel ?? 0)
  );

  const perksByTier = ordered.map((tier) => ({
    tier,
    perks: parsePerks(tier.perksJson),
    theme: resolveThemeToken(tier.themeColorToken),
  }));

  return (
    <section className="benefits-matrix" data-testid="benefits-matrix">
      <header className="benefits-matrix__header">
        <span className="benefits-matrix__icon" aria-hidden="true">
          <Crown size={16} />
        </span>
        <div className="benefits-matrix__title-block">
          <h3 className="benefits-matrix__title">Benefits by tier</h3>
          <p className="benefits-matrix__subtitle">
            Compare which loyalty promises are active across your ladder.
          </p>
        </div>
        {onEditBenefits ? (
          <button
            type="button"
            className="benefits-matrix__edit"
            onClick={onEditBenefits}
          >
            <Pencil size={14} />
            <span>Edit benefits</span>
          </button>
        ) : null}
      </header>

      <div className="benefits-matrix__table-wrap" role="region" aria-label="Benefits by tier table">
        {/* Tiers run down the left rail (rows); perks run across the top rail (columns). */}
        <table className="benefits-matrix__table">
          <thead>
            <tr>
              <th scope="col" className="benefits-matrix__col-corner">TIER</th>
              {CANONICAL_BENEFITS.map((benefit) => (
                <th
                  key={benefit}
                  scope="col"
                  className="benefits-matrix__col-benefit"
                  data-testid={`benefits-matrix-benefit-${benefit.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {benefit}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perksByTier.map(({ tier, perks, theme }) => (
              <tr key={tier.membershipTypeUuid}>
                <th
                  scope="row"
                  className="benefits-matrix__row-tier"
                  data-testid={`benefits-matrix-tier-${tier.themeColorToken}`}
                >
                  <span
                    className="benefits-matrix__tier-dot"
                    aria-hidden="true"
                    style={{ background: theme.swatchHex }}
                  />
                  <span className="benefits-matrix__tier-label">{tier.name.toUpperCase()}</span>
                </th>
                {CANONICAL_BENEFITS.map((benefit) => {
                  const active = tierAdvertisesBenefit(perks, benefit);
                  return (
                    <td
                      key={benefit}
                      className={`benefits-matrix__cell${active ? ' benefits-matrix__cell--active' : ''}`}
                      data-testid={`benefits-matrix-cell-${tier.themeColorToken}-${benefit.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {active ? (
                        <span className="benefits-matrix__check" aria-label="Included">
                          <Check size={12} />
                        </span>
                      ) : (
                        <span className="benefits-matrix__dash" aria-label="Not included">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default BenefitsByTierMatrix;
